import { useState } from 'react';
import { useStockPantry } from '../api/planner';
import type { ListItem } from '../api/planner';
import StockFields from '../components/StockFields';

/** "I already have this" — from the list, with what the pantry needs to know.
 *
 * The cart screen has had a one-tap version of this for a while, and one tap
 * is all it can be there: it is a hand-off to the shop, so anything it puts in
 * the pantry was bought today by definition. The shopping list is the other
 * case. The jar it is talking about is the one already in the cupboard,
 * noticed while reading the list, and dating that from today claims a shelf
 * life it does not have — a fortnight-old salsa recorded as fresh is exactly
 * the thing that stops the expiry warnings being worth reading.
 *
 * So the dates are askable, and every one of them is optional. The asking
 * itself lives in `StockFields`, which the pantry page's own add panel uses
 * too; what is particular to this screen is that the answer is about a line
 * on a shopping list, and that saying so makes the line disappear.
 */
export default function PantryStock({
  item,
  storeUnit,
}: {
  item: ListItem;
  /** What the recipes measure it in, as the amount's default wording. */
  storeUnit: string;
}) {
  const [open, setOpen] = useState(false);
  const stock = useStockPantry();

  if (stock.isSuccess) {
    // The line is on its way off the list — the pantry is consulted whenever
    // the list is built, so this says why it is about to disappear.
    return <span className="muted small">In the pantry ✓</span>;
  }

  return (
    <>
      <button
        type="button"
        className="link-button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? 'never mind' : 'already have it'}
      </button>

      {open && (
        <StockFields
          name={item.name}
          defaultUnit={storeUnit}
          packHint={`Leave the amount blank if the whole ${item.product_size || 'thing'} is still there.`}
          pending={stock.isPending}
          error={stock.isError ? (stock.error as Error).message : undefined}
          onSubmit={(row) =>
            stock.mutate({
              ...row,
              ingredient_id: item.ingredient_id,
              note: 'already had it',
            })
          }
        />
      )}
    </>
  );
}
